import type { GlobalStyleFieldSelectOption } from "@grapesjs/studio-sdk";

export const textTransforms = [
  {
    id: "lowercase",
    label: "Lowercase",
  },
  {
    id: "uppercase",
    label: "Uppercase",
  },
  {
    id: "capitalize",
    label: "Capitalize",
  },
  {
    id: "none",
    label: "None",
  },
];

export const emailSafeFontFamilies: GlobalStyleFieldSelectOption[] = [
  {
    id: "Arial, Helvetica, sans-serif",
    label: "Arial, Helvetica, sans-serif",
  },
  {
    id: "Verdana, Geneva, sans-serif",
    label: "Verdana, Geneva, sans-serif",
  },
  {
    id: "Tahoma, Geneva, sans-serif",
    label: "Tahoma, Geneva, sans-serif",
  },
  {
    id: "Trebuchet MS, Arial, sans-serif",
    label: "Trebuchet MS, Arial, sans-serif",
  },
  {
    id: "Georgia, Times New Roman, serif",
    label: "Georgia, Times New Roman, serif",
  },
  {
    id: "Times New Roman, Times, serif",
    label: "Times New Roman, Times, serif",
  },
  {
    id: "Palatino Linotype, Book Antiqua, Palatino, serif",
    label: "Palatino Linotype, Book Antiqua, Palatino, serif",
  },
  {
    id: "Courier New, Courier, monospace",
    label: "Courier New, Courier, monospace",
  },
];

export const fontWeights = [
  // The keywords are the same weights as 400 / 700 in CSS, so they carry the
  // numeric labels instead of showing up as a second Regular and Bold.
  {
    id: "normal",
    label: "Regular (400)",
  },
  {
    id: "bold",
    label: "Bold (700)",
  },
  {
    id: "800",
    label: "Extra Bold (800)",
  },
  // Picked from the menu before the keywords took these labels: still
  // resolved for values already stored, never offered again.
  {
    id: "400",
    label: "Regular (400)",
    hidden: true,
  },
  {
    id: "700",
    label: "Bold (700)",
    hidden: true,
  },
];

/**
 * The email-safe stack a web font falls back to when a client blocks the
 * download — Outlook desktop always does. Arial behind a serif face reflows
 * the whole email, so the fallback follows the face's shape instead: a serif
 * degrades to Georgia, a monospace to Courier, everything else to Arial.
 *
 * ponytail: name heuristic, because the kit gives us a family name and no
 * classification. A serif whose name says nothing (Recoleta, Canela) still
 * falls back to Arial, exactly as everything does today. Have Prisma report
 * the font's generic family and this can read it instead of guessing.
 */
export const fallbackStackFor = (family: string): string => {
  const name = family.toLowerCase();
  // Whole words, so "Encode Sans" and "Monoton" stay sans; "consol" stays a
  // substring for Inconsolata.
  if (/\bmono\b|\bcode\b|courier|consol/.test(name))
    return "Courier New, Courier, monospace";
  // "Open Sans", "Alegreya Sans" — checked first so the serif words below
  // cannot claim a face that already says it is a sans.
  if (/\bsans\b/.test(name)) return "Arial, Helvetica, sans-serif";
  if (
    /serif|slab|times|roman|garamond|playfair|merriweather|lora|baskerville|cormorant|crimson|spectral|bodoni|didot|caslon/.test(
      name,
    )
  )
    return "Georgia, Times New Roman, serif";
  return "Arial, Helvetica, sans-serif";
};

/**
 * The brand's own web fonts, first in a font picker's options.
 *
 * Every picker is a strict select over `emailSafeFontFamilies`, so a brand face
 * like Barlow has no option to select until its brand kit names one. A kit names
 * up to two — a display face for the headings and a text face for the body — and
 * both belong in every picker, since either can be the right one for a given
 * slot. Names arrive from the styling record untyped: anything that is not a
 * non-empty string is skipped, and a family named twice is offered once, so a
 * brand with one face (or none) gets exactly today's list.
 */
export const withWebFont = (
  options: GlobalStyleFieldSelectOption[],
  ...families: unknown[]
): GlobalStyleFieldSelectOption[] => [
  ...families
    .filter(
      (family, i): family is string =>
        typeof family === "string" &&
        !!family &&
        // Could close the font-family declaration or the attribute it lands in.
        !/["'\\;,{}<>]/.test(family) &&
        families.indexOf(family) === i,
    )
    .map((family) => ({
      id: `${cssFamilyName(family)}, ${fallbackStackFor(family)}`,
      label: family,
    })),
  ...options,
];

/**
 * A family as it must appear in a font-family list: bare when every word is a
 * CSS identifier, single-quoted otherwise ("Baloo 2") — unquoted, the whole
 * declaration is invalid and the client drops to its default face.
 * branddetails-v2's webFontStack mirrors this and the fallback rule: the
 * "<family>, <fallback>" it prefills must equal this option id.
 */
const cssFamilyName = (family: string): string =>
  family.split(/\s+/).every((word) => /^-?[_a-zA-Z][\w-]*$/.test(word))
    ? family
    : `'${family}'`;

const leadingFamily = (stack: string): string =>
  stack
    .split(",")[0]
    .trim()
    .replace(/^["']|["']$/g, "")
    .toLowerCase();

/**
 * The option a stored font stack selects: its exact id, else the option for
 * the same leading family — a stack saved with another fallback or quoting is
 * still that font.
 */
export const findFontOption = <T extends { id: string | number }>(
  options: T[],
  value: string,
): T | undefined =>
  options.find((option) => String(option.id) === value) ??
  (leadingFamily(value)
    ? options.find(
        (option) => leadingFamily(String(option.id)) === leadingFamily(value),
      )
    : undefined);

/**
 * The families the brand kit's web-font pairs name, headline first — the order
 * `withWebFont` offers them in, and the order Prisma measures them in.
 */
export const webFontFamiliesOf = (values: {
  webFontFamily?: unknown;
  bodyWebFontFamily?: unknown;
}): unknown[] => [values.webFontFamily, values.bodyWebFontFamily];

export const fontStyles = [
  {
    id: "normal",
    label: "Normal",
  },
  {
    id: "italic",
    label: "Italic",
  },
];

export const underlineOptions = [
  {
    id: "underline",
    label: "Underline",
  },
  {
    id: "none",
    label: "No underline",
  },
];

export const borderStyles = [
  {
    id: "none",
    label: "None",
  },
  {
    id: "solid",
    label: "Solid",
  },
  {
    id: "dotted",
    label: "Dotted",
  },
  {
    id: "dashed",
    label: "Dashed",
  },
  {
    id: "double",
    label: "Double",
  },
  {
    id: "groove",
    label: "Groove",
  },
  {
    id: "ridge",
    label: "Ridge",
  },
  {
    id: "inset",
    label: "Inset",
  },
  {
    id: "outset",
    label: "Outset",
  },
];

export const languages = [
  {
    id: "en-US",
    label: "English (US)",
  },
];

export const emojiUsageLevels = [
  {
    id: 0,
    label: "None — keep it professional",
  },
  {
    id: 1,
    label: "Minimal — just a few for tone",
  },
  {
    id: 2,
    label: "Moderate — balanced approach",
  },
  {
    id: 3,
    label: "High — expressive and engaging",
  },
];
